/**
 * visual-review: a design-option board. One decision, options A–D… each with images or live HTML
 * boards (light and dark). The user compares two options side by side or as a slider, comments per
 * option, and picks one (keys 1–9, or Enter on the focused tile) or mixes by area ("C for buttons,
 * D for toolbars"). Presentational: the container supplies `renderMedia` (decrypting on this
 * device) and receives the pick through the decision view's answer bar.
 */
import { type OptionMedia, optionLetter } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { Columns2, MessageSquare, Moon, Sun } from 'lucide-react'
import { type ReactNode, useId, useMemo, useRef, useState } from 'react'
import type { DecisionView as DecisionModel, OptionView } from '../../lib/decision-view.js'
import { currentTheme } from '../../lib/theme.js'
import { type CompareMode, CompareModeSwitch, MediaSlider, MediaSplit } from '../media-compare.js'
import './option-board.css'
import {
  moveOnArrow,
  type OptionCursor,
  OptionKey,
  optionState,
  SuggestedPill,
} from './OptionList.js'

export type BoardTheme = 'light' | 'dark'

export interface BoardState {
  comments: Record<string, string>
  setComment: (optionId: string, text: string) => void
  compare: string[]
  toggleCompare: (optionId: string) => void
  theme: BoardTheme
  setTheme: (t: BoardTheme) => void
  /** Comments typed: a number key then picks (marks) instead of answering at once. */
  dirty: boolean
  /** What rides with the answer (empty when nothing was composed). */
  extra: () => {
    comments?: Record<string, string>
  }
  reset: () => void
}

/** Option previews start in the theme the app renders (dark by default), not the OS one. */
const prefersDark = () => currentTheme() === 'dark'

export function useBoardState(v: Pick<DecisionModel, 'key' | 'board' | 'options'>): BoardState {
  const [comments, setComments] = useState<Record<string, string>>({})
  const [compare, setCompare] = useState<string[]>([])
  const [theme, setTheme] = useState<BoardTheme>(prefersDark() ? 'dark' : 'light')
  // Reset per decision (the container keeps one state across list navigation).
  const [forKey, setForKey] = useState(v.key)
  if (forKey !== v.key) {
    setForKey(v.key)
    setComments({})
    setCompare([])
  }
  const clean = Object.fromEntries(Object.entries(comments).filter(([, t]) => t.trim()))
  return {
    comments,
    setComment: (id, text) => setComments((c) => ({ ...c, [id]: text })),
    compare,
    toggleCompare: (id) =>
      setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id].slice(-2))),
    theme,
    setTheme,
    dirty: Object.keys(clean).length > 0,
    // No "Mix": combining directions goes in the note ("C, but with A's header").
    extra: () => (Object.keys(clean).length ? { comments: clean } : {}),
    reset: () => {
      setComments({})
      setCompare([])
    },
  }
}

/** The visuals of an option for a colour scheme (unthemed ones always; else the scheme's). */
export function mediaFor(o: Pick<OptionView, 'media'>, theme: BoardTheme): OptionMedia[] {
  const themed = o.media.filter((m) => m.theme)
  const plain = o.media.filter((m) => !m.theme)
  const match = themed.filter((m) => m.theme === theme)
  return [...plain, ...(match.length ? match : themed)]
}

export type RenderMedia = (m: OptionMedia, o: { title: string }) => ReactNode

/** The visuals of one option: the hero (inside the radio) and its switcher (outside it). */
function useVisuals(option: OptionView, board: BoardState) {
  const list = mediaFor(option, board.theme)
  const [i, setI] = useState(0)
  const at = Math.min(i, Math.max(list.length - 1, 0))
  return { list, at, setI, cur: list[at] }
}

function Visual({
  cur,
  title,
  renderMedia,
}: {
  cur: OptionMedia | undefined
  title: string
  renderMedia: RenderMedia
}) {
  if (!cur) return <div className="ob-media ob-media--none">No visual</div>
  return <div className="ob-media">{renderMedia(cur, { title })}</div>
}

function OptionTile({
  view: v,
  option: o,
  letter,
  state,
  isCursor,
  compared,
  areas,
  disabled,
  board,
  renderMedia,
  cur,
  onPick,
}: {
  view: DecisionModel
  option: OptionView
  letter: string
  state: ReturnType<typeof optionState>
  isCursor: boolean
  compared: boolean
  areas: string[]
  disabled: boolean
  board: BoardState
  renderMedia: RenderMedia
  cur: OptionCursor
  onPick: (o: OptionView) => void
}) {
  const id = useId()
  const vis = useVisuals(o, board)
  const [commenting, setCommenting] = useState(false)
  const answer = v.decision?.answer
  const on = state.picked
  const mark = () => {
    cur.setPicked(o.id)
    cur.setCursor(o.id)
  }
  return (
    <div className={`ob-card ${state.className}`}>
      {/* biome-ignore lint/a11y/useSemanticElements: a rich option tile is not a native radio */}
      <div
        role="radio"
        aria-checked={on}
        aria-disabled={disabled || undefined}
        aria-label={`Option ${letter}: ${o.label}`}
        tabIndex={disabled ? -1 : isCursor ? 0 : -1}
        className="ob-opt"
        data-option={o.key}
        data-option-id={o.id}
        data-letter={letter}
        onFocus={() => cur.setCursor(o.id)}
        onClick={() => !disabled && mark()}
        onDoubleClick={() => !disabled && onPick(o)}
        onKeyDown={(e) => {
          if (disabled) return
          if (e.key === 'Enter') {
            e.preventDefault()
            onPick(o)
          } else if (e.key === ' ') {
            e.preventDefault()
            mark()
          } else if (e.key === 'c' || e.key === 'C') {
            e.preventDefault()
            board.toggleCompare(o.id)
          } else moveOnArrow(e, cur, { horizontal: true })
        }}
      >
        <Visual cur={vis.cur} title={`${letter} ${o.label}`} renderMedia={renderMedia} />
        <div className="ob-opt__top">
          <OptionKey n={o.key} picked={on} />
          <span className="ob-opt__label">
            {letter} · {o.label}
          </span>
          {o.recommended ? <SuggestedPill /> : null}
          {areas.length ? <span className="ob-opt__areas">for {areas.join(', ')}</span> : null}
        </div>
        {o.rationale || o.description ? (
          <p className="ob-opt__why">{o.rationale || o.description}</p>
        ) : null}
      </div>
      {/* The controls sit beside the radio, never inside it (interactive content is not nestable). */}
      <div className="ob-opt__tools">
        {o.pros.length || o.cons.length ? (
          <details className="ob-more">
            <summary>Trade-offs</summary>
            <div className="dv-opt__tradeoffs">
              {o.pros.length ? (
                <ul aria-label={`For ${o.label}`}>
                  {o.pros.map((x) => (
                    <li key={x} className="is-pro">
                      {x}
                    </li>
                  ))}
                </ul>
              ) : null}
              {o.cons.length ? (
                <ul aria-label={`Against ${o.label}`}>
                  {o.cons.map((x) => (
                    <li key={x} className="is-con">
                      {x}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </details>
        ) : null}
        {vis.list.length > 1 ? (
          <div className="ob-thumbs" role="group" aria-label={`Visuals of option ${letter}`}>
            {vis.list.map((m, n) => (
              <button
                key={`${m.ref}:${m.theme ?? ''}`}
                type="button"
                className={`ob-thumb${n === vis.at ? ' is-on' : ''}`}
                aria-pressed={n === vis.at}
                onClick={() => vis.setI(n)}
              >
                {m.label ?? `${m.kind === 'html' ? 'Board' : 'Image'} ${n + 1}`}
              </button>
            ))}
          </div>
        ) : null}
        <div className="ob-tools__row">
          <label className="ob-check">
            <input
              type="checkbox"
              checked={compared}
              disabled={disabled && !v.open}
              onChange={() => board.toggleCompare(o.id)}
            />
            <span>
              <Columns2 size={16} aria-hidden="true" /> Compare {letter}
            </span>
          </label>
          {v.open ? (
            <button
              type="button"
              className="ob-tool"
              aria-expanded={commenting}
              aria-controls={`${id}-c`}
              onClick={() => setCommenting((x) => !x)}
            >
              <MessageSquare size={16} aria-hidden="true" />
              {board.comments[o.id]?.trim() ? 'Comment ✓' : 'Comment'}
            </button>
          ) : null}
        </div>
        {v.open && commenting ? (
          <>
            <label htmlFor={`${id}-c`} className="ob-comment__label">
              Comment on {letter} ({o.label})
            </label>
            <textarea
              id={`${id}-c`}
              className="ob-comment"
              rows={2}
              placeholder="What to change, keep or drop"
              value={board.comments[o.id] ?? ''}
              disabled={disabled}
              onChange={(e) => board.setComment(o.id, e.target.value)}
            />
          </>
        ) : answer?.comments?.[o.id] ? (
          <p className="ob-comment__said">
            <strong>Your comment:</strong> {answer.comments[o.id]}
          </p>
        ) : null}
      </div>
    </div>
  )
}

export function OptionBoard({
  view: v,
  cur,
  active,
  onPick,
  disabled,
  board,
  renderMedia,
  labelledBy,
}: {
  view: DecisionModel
  cur: OptionCursor
  /** The decision owns the keyboard: the cursor ring shows without DOM focus. */
  active: boolean
  onPick: (o: OptionView) => void
  disabled: boolean
  board: BoardState
  renderMedia: RenderMedia
  labelledBy: string
}) {
  const group = useRef<HTMLDivElement>(null)
  const [mode, setMode] = useState<CompareMode>('side')
  const letters = useMemo(() => new Map(v.options.map((o, i) => [o.id, optionLetter(i + 1)])), [v])
  const byId = new Map(v.options.map((o) => [o.id, o]))
  const themed = v.options.some((o) => o.media.some((m) => m.theme))
  const [a, b] = board.compare.map((x) => byId.get(x))
  const heroOf = (o: OptionView) => mediaFor(o, board.theme)[0]
  const stills = !!a && !!b && [a, b].every((o) => heroOf(o)?.kind === 'image')
  const answer = v.decision?.answer
  const chosen = new Map<string, string[]>()
  if (!v.open && answer) {
    if (answer.mix?.length)
      for (const m of answer.mix)
        chosen.set(m.optionId, [...(chosen.get(m.optionId) ?? []), m.area])
    else if (answer.optionId) chosen.set(answer.optionId, [])
  }
  const gate = v.board?.gate

  return (
    <div className="ob" data-board>
      <div className="ob-bar">
        <span className="ob-count">
          {v.options.length} {v.options.every((o) => o.media.length) ? 'mockups' : 'options'}
        </span>
        {gate ? (
          <p className="ob-gate" data-gate={gate.state} role="status">
            {gate.state === 'waiting'
              ? `Implementation waits for sign-off: ${gate.tasks} ${gate.tasks === 1 ? 'task is' : 'tasks are'} on hold until you choose.`
              : gate.state === 'signed-off'
                ? `Signed off. ${gate.tasks} ${gate.tasks === 1 ? 'task was' : 'tasks were'} released${v.board?.fanOut ? `, and the choice was added to ${v.board.fanOut} ${v.board.fanOut === 1 ? 'task' : 'tasks'}` : ''}.`
                : 'The agent withdrew the options. The held tasks were released.'}
          </p>
        ) : v.board?.fanOut ? (
          <p className="ob-gate" role="status">
            Your choice goes to {v.board.fanOut} affected {v.board.fanOut === 1 ? 'task' : 'tasks'}.
          </p>
        ) : null}
        {themed ? (
          <div className="ob-theme" role="radiogroup" aria-label="Preview colour scheme">
            {(['light', 'dark'] as const).map((t) => (
              // biome-ignore lint/a11y/useSemanticElements: segmented control
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={board.theme === t}
                className={`mc-modes__b${board.theme === t ? ' is-on' : ''}`}
                onClick={() => board.setTheme(t)}
              >
                {t === 'light' ? (
                  <Sun size={14} aria-hidden="true" />
                ) : (
                  <Moon size={14} aria-hidden="true" />
                )}{' '}
                {t === 'light' ? 'Light' : 'Dark'}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {a && b ? (
        <section
          className="ob-compare"
          aria-label={`Compare ${letters.get(a.id)} and ${letters.get(b.id)}`}
        >
          <header className="ob-compare__head">
            <h4>
              {letters.get(a.id)} ({a.label}) vs {letters.get(b.id)} ({b.label})
            </h4>
            <CompareModeSwitch
              mode={mode}
              onChange={setMode}
              sliderOk={stills}
              label="Compare mode"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => board.toggleCompare(a.id) /* drop the older */}
            >
              Close compare
            </Button>
          </header>
          {(() => {
            const ma = heroOf(a)
            const mb = heroOf(b)
            const ra = ma ? renderMedia(ma, { title: `${letters.get(a.id)} ${a.label}` }) : null
            const rb = mb ? renderMedia(mb, { title: `${letters.get(b.id)} ${b.label}` }) : null
            return mode === 'slider' && stills ? (
              <MediaSlider
                before={ra}
                after={rb}
                beforeLabel={`${letters.get(a.id)} ${a.label}`}
                afterLabel={`${letters.get(b.id)} ${b.label}`}
              />
            ) : (
              <MediaSplit
                left={ra}
                right={rb}
                leftLabel={`${letters.get(a.id)} · ${a.label}`}
                rightLabel={`${letters.get(b.id)} · ${b.label}`}
              />
            )
          })()}
        </section>
      ) : null}

      {/* biome-ignore lint/a11y/useSemanticElements: a rich option tile is not a native radio */}
      <div ref={group} className="ob-grid" role="radiogroup" aria-labelledby={labelledBy}>
        {v.options.map((o) => (
          <OptionTile
            key={o.id}
            view={v}
            option={o}
            letter={letters.get(o.id) as string}
            state={optionState(o, {
              cursor: cur.cursor,
              picked: v.open ? cur.picked : chosen.has(o.id) ? o.id : undefined,
              active: active && v.open,
            })}
            isCursor={cur.cursor === o.id}
            compared={board.compare.includes(o.id)}
            areas={chosen.get(o.id) ?? []}
            disabled={disabled}
            board={board}
            renderMedia={renderMedia}
            cur={cur}
            onPick={onPick}
          />
        ))}
      </div>
    </div>
  )
}
